'use client'

import { useEffect, useRef } from 'react'
import { Button, Icon, Image, Text } from '@vinted/web-ui'
import { HeartFilled16 } from '@vinted/monochrome-icons'

import { HOW_IT_WORKS_URL } from 'constants/routes'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'

import SellButton from '../SellButton'
import { IMAGE_BASE_URL } from '../constants'

type CarouselItem = {
  imageName: string
  titleKey: string
  priceKey: string
  isFavorite: boolean
}

const SCROLL_SPEED = 20
const CAROUSEL_ITEMS: Array<Array<CarouselItem>> = [
  [
    {
      imageName: 'example_listing_1',
      titleKey: 'example_listings.item_1.title',
      priceKey: 'example_listings.item_1.price',
      isFavorite: false,
    },
    {
      imageName: 'example_listing_2',
      titleKey: 'example_listings.item_2.title',
      priceKey: 'example_listings.item_2.price',
      isFavorite: true,
    },
    {
      imageName: 'example_listing_3',
      titleKey: 'example_listings.item_3.title',
      priceKey: 'example_listings.item_3.price',
      isFavorite: false,
    },
    {
      imageName: 'example_listing_4',
      titleKey: 'example_listings.item_4.title',
      priceKey: 'example_listings.item_4.price',
      isFavorite: true,
    },
    {
      imageName: 'example_listing_5',
      titleKey: 'example_listings.item_5.title',
      priceKey: 'example_listings.item_5.price',
      isFavorite: false,
    },
  ],
  [
    {
      imageName: 'example_listing_6',
      titleKey: 'example_listings.item_6.title',
      priceKey: 'example_listings.item_6.price',
      isFavorite: true,
    },
    {
      imageName: 'example_listing_7',
      titleKey: 'example_listings.item_7.title',
      priceKey: 'example_listings.item_7.price',
      isFavorite: false,
    },
    {
      imageName: 'example_listing_8',
      titleKey: 'example_listings.item_8.title',
      priceKey: 'example_listings.item_8.price',
      isFavorite: true,
    },
    {
      imageName: 'example_listing_9',
      titleKey: 'example_listings.item_9.title',
      priceKey: 'example_listings.item_9.price',
      isFavorite: false,
    },
    {
      imageName: 'example_listing_10',
      titleKey: 'example_listings.item_10.title',
      priceKey: 'example_listings.item_10.price',
      isFavorite: true,
    },
  ],
] as const

const Carousel = ({
  items,
  direction,
}: {
  items: Array<CarouselItem>
  direction: 'UP' | 'DOWN'
}) => {
  const translate = useTranslate('business.digital_product_passport.sell')
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!ref.current) {
      return () => undefined
    }

    const carousel = ref.current
    const firstRepeatedItem = carousel.children.item(
      direction === 'UP' ? items.length - 1 : items.length,
    ) as HTMLDivElement
    const initialScrollTop = direction === 'UP' ? carousel.scrollHeight : 0

    carousel.scrollTop = initialScrollTop
    const interval = setInterval(() => {
      if (direction === 'UP') {
        carousel.scrollTop -= 0.5

        if (
          carousel.scrollTop <=
          firstRepeatedItem.offsetTop - carousel.clientHeight + firstRepeatedItem.clientHeight
        ) {
          carousel.scrollTop = initialScrollTop
        }
      }

      if (direction === 'DOWN') {
        carousel.scrollTop += 0.5

        if (carousel.scrollTop >= firstRepeatedItem.offsetTop) {
          carousel.scrollTop = initialScrollTop
        }
      }
    }, SCROLL_SPEED)

    return () => {
      clearInterval(interval)
    }
  }, [direction, items.length])

  return (
    <div className="digital-product-passport_carousel" ref={ref}>
      {[...items, ...items].map((item, index) => (
        <div className="digital-product-passport_carousel-item" key={index}>
          <div className="digital-product-passport_carousel-item-image">
            <Image
              src={`${IMAGE_BASE_URL}/${item.imageName}.jpg`}
              srcset={`${IMAGE_BASE_URL}/${item.imageName}.jpg 1x, ${IMAGE_BASE_URL}/${item.imageName}@2x.jpg 2x, ${IMAGE_BASE_URL}/${item.imageName}@3x.jpg 3x`}
              alt={translate(item.titleKey)}
            />
            {item.isFavorite && (
              <div className="digital-product-passport_carousel-item-favourite">
                <Icon name={HeartFilled16} color={Icon.Color.WarningMedium} />
              </div>
            )}
          </div>
          <div className="digital-product-passport_carousel-item-content">
            <Text as="span">{translate(item.titleKey)}</Text>
            <Text as="span">{translate(item.priceKey)}</Text>
          </div>
        </div>
      ))}
    </div>
  )
}

const SellSection = ({ dppRef }: { dppRef: string }) => {
  const translate = useTranslate('business.digital_product_passport.sell')

  const renderCarousels = () => {
    return CAROUSEL_ITEMS.map((items, index) => {
      return <Carousel key={index} items={items} direction={index % 2 === 0 ? 'UP' : 'DOWN'} />
    })
  }

  const renderButtons = (className: string) => {
    return (
      <div className={`digital-product-passport_buttons ${className}`}>
        <SellButton dppRef={dppRef} />
        <Button url={HOW_IT_WORKS_URL} testId="digital-product-passport-learn-more-button">
          {translate('learn_more_button.title')}
        </Button>
      </div>
    )
  }

  return (
    <section className="digital-product-passport_sell-section">
      <div className="digital-product-passport_container">
        <div className="digital-product-passport_left">
          <Text as="h2">{translate('title')}</Text>
          <Text as="p">{translate('description')}</Text>
          {renderButtons('digital-product-passport_desktop-buttons')}
        </div>
        <div className="digital-product-passport_right">{renderCarousels()}</div>
      </div>
      {renderButtons('digital-product-passport_mobile-buttons')}
    </section>
  )
}

export default SellSection
